import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route, useLocation, useParams } from "react-router-dom";
import { useEffect, Suspense } from "react";
import { lazyWithReload } from "@/lib/lazyWithReload";
const Admin = lazyWithReload(() => import("./pages/admin/Admin"), "Admin");
const ManageParticipants = lazyWithReload(() => import("./pages/admin/ManageParticipants"), "ManageParticipants");
const ManageEvents = lazyWithReload(() => import("./pages/admin/ManageEvents"), "ManageEvents");
const ManageLocations = lazyWithReload(() => import("./pages/admin/ManageLocations"), "ManageLocations");
const ManagePeople = lazyWithReload(() => import("./pages/admin/ManagePeopleTeams"), "ManagePeople");
const Admins = lazyWithReload(() => import("./pages/admin/Admins"), "Admins");
const ImportQuestions = lazyWithReload(() => import("./pages/admin/ImportQuestions"), "ImportQuestions");
const ManagePromocodes = lazyWithReload(() => import("./pages/admin/ManagePromocodes"), "ManagePromocodes");
const ManagePayments = lazyWithReload(() => import("./pages/admin/ManagePayments"), "ManagePayments");
const Play = lazyWithReload(() => import("./pages/Play"), "Play");
const PlaySubmitted = lazyWithReload(() => import("./pages/PlaySubmitted"), "PlaySubmitted");
const Profile = lazyWithReload(() => import("./pages/Profile"), "Profile");
const UploadTrack = lazyWithReload(() => import("./pages/UploadTrack"), "UploadTrack");
const London = lazyWithReload(() => import("./pages/London"), "London");
const EventResults = lazyWithReload(() => import("./pages/EventResults"), "EventResults");
const EventAnswers = lazyWithReload(() => import("./pages/EventAnswers"), "EventAnswers");
const TrackInfo = lazyWithReload(() => import("./pages/TrackInfo"), "TrackInfo");
const Messaging = lazyWithReload(() => import("./pages/admin/Messaging"), "Messaging");
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"), "ResetPassword");
const ProfileEmail = lazyWithReload(() => import("./pages/ProfileEmail"), "ProfileEmail");
import { ScrollToTop } from "./components/ScrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import { AuthModal } from "./components/auth/AuthModal";
import { ProfileCompletionDialog } from "./components/profile/ProfileCompletionDialog";
import { Layout } from "./components/Layout";
import Index from "./pages/Index";
import Events from "./pages/Events";
import EventsRegister from "./pages/EventsRegister";
// Eager, not lazy: maplibre map fails to paint when Geography mounts via
// Suspense (blank map on prod since #339). Eager mount matches the known-good
// staging behaviour; maplibre stays off the homepage via its own manualChunk.
import Geography from "./pages/Geography";


const ParticipantResults = lazyWithReload(() => import("./pages/ParticipantResults"), "ParticipantResults");
const TeamResults = lazyWithReload(() => import("./pages/TeamResults"), "TeamResults");
import Partnership from "./pages/Partnership";

import NotFound from "./pages/NotFound";
import Support from "./pages/Support";


import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Credits from "./pages/Credits";
import Unsubscribe from "./pages/Unsubscribe";
import NotThisTime from "./pages/NotThisTime";
import { RequireRole } from "./components/auth/RequireRole";



const queryClient = new QueryClient();

const ScrollToHash = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const timer = setTimeout(() => {
      // Auth redirects land with '#access_token=…' / '#error=…', which aren't
      // valid selectors — querySelector would throw.
      let el: Element | null = null;
      try {
        el = document.querySelector(hash);
      } catch {
        return;
      }
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 300);
    return () => clearTimeout(timer);
  }, [hash]);
  return null;
};

// Remount EventResults per quizrun so its filter/sort state re-inits from the
// new quizrun's URL/saved values instead of leaking across SPA navigation
// (React Router reuses the instance when only the :quizrunId param changes).
const EventResultsRoute = () => {
  const { quizrunId } = useParams<{ quizrunId: string }>();
  return <EventResults key={quizrunId} />;
};

// The track page used to live under /admin; keep old links working.
const LegacyTrackRedirect = () => {
  const { participationId } = useParams<{ participationId: string }>();
  const { search } = useLocation();
  return <Navigate to={`/track/${participationId}${search}`} replace />;
};

const App = () => {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ImpersonationProvider>
            <TooltipProvider>
            <Toaster />
            <BrowserRouter>
              <ScrollToTop />
              <ScrollToHash />
              <ProfileCompletionDialog />
              <AuthModal />
              <Suspense fallback={<div className="flex min-h-[60vh] items-center justify-center" role="status"><div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-brand-orange" aria-hidden="true" /><span className="sr-only">Loading</span></div>}><Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/events" element={<Events />} />
                  <Route
                    path="/events/register"
                    element={<EventsRegister />}
                  />
                  <Route path="/events/:quizrunId" element={<EventResultsRoute />} />
                  <Route
                    path="/events/:quizrunId/answers"
                    element={
                      <RequireRole role="authenticated">
                        <EventAnswers />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="/participant/:participantId"
                    element={<ParticipantResults />}
                  />
                  <Route path="/team/:teamId" element={<TeamResults />} />
                  <Route path="/partnership" element={<Partnership />} />
                  <Route path="/geography" element={<Geography />} />
                  <Route path="/london" element={<London />} />
                  <Route
                    path="/track/:participationId"
                    element={
                      <RequireRole role="authenticated">
                        <TrackInfo />
                      </RequireRole>
                    }
                  />
                  <Route
                    path="/upload-track"
                    element={
                      <RequireRole role="authenticated">
                        <UploadTrack />
                      </RequireRole>
                    }
                  />
                  <Route path="/support" element={<Support />} />
                  <Route path="/play" element={<RequireRole role="authenticated"><Play /></RequireRole>} />
                  <Route path="/play/submitted" element={<RequireRole role="authenticated"><PlaySubmitted /></RequireRole>} />
                  <Route path="/about" element={<About />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/credits" element={<Credits />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/not-this-time" element={<NotThisTime />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/profile-email" element={<ProfileEmail />} />
                  <Route path="/profile" element={<RequireRole role="authenticated"><Profile /></RequireRole>} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/participants" element={<ManageParticipants />} />
                  <Route path="/admin/manage-events" element={<ManageEvents />} />
                  <Route path="/admin/locations" element={<ManageLocations />} />
                  <Route path="/admin/people" element={<ManagePeople />} />
                  <Route path="/admin/admins" element={<Admins />} />
                  <Route path="/admin/promocodes" element={<ManagePromocodes />} />
                  <Route path="/admin/payments" element={<ManagePayments />} />
                  <Route path="/admin/import-questions" element={<ImportQuestions />} />
                  <Route path="/admin/track/:participationId" element={<LegacyTrackRedirect />} />
                  <Route path="/admin/messaging" element={<Messaging />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes></Suspense>
            </BrowserRouter>
            </TooltipProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
};

export default App;
